import React, { useEffect, useRef, useState } from 'react';
import { X, Hash, Maximize2, Minimize2, ExternalLink, Link2, Pin, Sparkles, Loader2 } from 'lucide-react';
import { MindMapNode, Language, SemanticSearchResult } from '../types';
import { parseMarkdown } from '../utils/markdownUtils';
import { semanticSearchNotes } from '../services/geminiService';
import { db, auth } from '../services/firebase';
import { doc, getDoc } from 'firebase/firestore';

interface Props {
    tag: string;
    notes: MindMapNode[];
    language: Language;
    onClose: () => void;
    onNavigate: (nodeId: string) => void;
    onNavigateCrossNotebook?: (notebookId: string, nodeId: string) => void;
    onCreateLink?: (nodeId: string) => void;
    linkedNodeIds?: Set<string>;
}

interface HoverState {
    node: MindMapNode;
    top: number;
    left: number;
}

type EnrichedResult = SemanticSearchResult & {
    imageBase64?: string | null;
    fullContent?: string;
    fetched?: boolean;
};

const PREVIEW_W_NORMAL = 300;
const PREVIEW_W_EXPANDED = 500;
const PREVIEW_H = 420;
const HOVER_DELAY = 250;
const LEAVE_DELAY = 120;

const getFirstImage = (node: MindMapNode): string | null =>
    node.imageBase64 || node.attachments?.find(a => a.type === 'image')?.content || null;

export const TagPreviewStrip: React.FC<Props> = ({ tag, notes, language, onClose, onNavigate, onNavigateCrossNotebook, onCreateLink, linkedNodeIds }) => {
    const isVi = language === 'vietnamese';
    const displayTag = tag.startsWith('#') ? tag : `#${tag}`;

    const [hover, setHover] = useState<HoverState | null>(null);
    const [isHoverExpanded, setIsHoverExpanded] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [pinnedPreviews, setPinnedPreviews] = useState<Array<{ id: string; node: MindMapNode; x: number; y: number; expanded: boolean }>>([]);
    const draggingPinRef = useRef<{ id: string; offsetX: number; offsetY: number } | null>(null);

    const [semanticResults, setSemanticResults] = useState<EnrichedResult[]>([]);
    const [semanticLoading, setSemanticLoading] = useState(false);
    const [semanticSearched, setSemanticSearched] = useState(false);

    useEffect(() => {
        const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handler);
        return () => {
            document.removeEventListener('keydown', handler);
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [onClose]);

    useEffect(() => {
        const onMove = (e: MouseEvent) => {
            if (!draggingPinRef.current) return;
            const { id, offsetX, offsetY } = draggingPinRef.current;
            setPinnedPreviews(prev => prev.map(p => p.id === id ? { ...p, x: e.clientX - offsetX, y: e.clientY - offsetY } : p));
        };
        const onUp = () => { draggingPinRef.current = null; };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    }, []);

    const clearTimer = () => { if (timerRef.current) clearTimeout(timerRef.current); };

    const showHover = (node: MindMapNode, cardEl: HTMLElement) => {
        clearTimer();
        timerRef.current = setTimeout(() => {
            const rect = cardEl.getBoundingClientRect();
            const w = PREVIEW_W_NORMAL;
            let left = rect.left + rect.width / 2 - w / 2;
            left = Math.max(8, Math.min(window.innerWidth - w - 8, left));
            const top = Math.max(8, rect.top - PREVIEW_H - 10);
            setIsHoverExpanded(false);
            setHover({ node, top, left });
        }, HOVER_DELAY);
    };

    const showSemanticHover = (result: EnrichedResult, cardEl: HTMLElement) => {
        clearTimer();
        timerRef.current = setTimeout(() => {
            const rect = cardEl.getBoundingClientRect();
            const w = PREVIEW_W_NORMAL;
            let left = rect.left + rect.width / 2 - w / 2;
            left = Math.max(8, Math.min(window.innerWidth - w - 8, left));
            const top = Math.max(8, rect.top - PREVIEW_H - 10);
            setIsHoverExpanded(false);
            const stubNode: MindMapNode = {
                id: result.nodeId,
                title: result.nodeTitle || '',
                content: result.fullContent || result.excerpt || '',
                parentId: undefined,
                position: { x: 0, y: 0 },
                imageBase64: result.imageBase64 ?? null,
                imageMimeType: null,
                analysis: null,
                isAnalyzing: false,
                isExpanded: false,
                createdAt: 0,
                updatedAt: 0,
            };
            setHover({ node: stubNode, top, left });
        }, HOVER_DELAY);
    };

    const hideHover = () => {
        clearTimer();
        timerRef.current = setTimeout(() => { setHover(null); setIsHoverExpanded(false); }, LEAVE_DELAY);
    };

    const navigateToNote = (nodeId: string) => {
        onNavigate(nodeId);
        onClose();
    };

    const handleSemanticSearch = async () => {
        if (semanticSearched || semanticLoading) return;
        setSemanticLoading(true);
        setSemanticSearched(true);
        try {
            const query = tag.replace(/^#/, '').replace(/-/g, ' ');
            const res = await semanticSearchNotes(query);
            const exactIds = new Set(notes.map(n => n.id));
            const filtered: EnrichedResult[] = res.filter(r => !exactIds.has(r.nodeId) && !r.lowConfidence);
            setSemanticResults(filtered);

            // Fetch images + full content in background for each result
            const uid = auth.currentUser?.uid;
            if (uid && filtered.length > 0) {
                Promise.all(filtered.map(async (r) => {
                    try {
                        const snap = await getDoc(doc(db, `users/${uid}/notebooks/${r.notebookId}/nodes/${r.nodeId}`));
                        if (snap.exists()) {
                            const d = snap.data();
                            return { ...r, imageBase64: d.imageBase64 || null, fullContent: d.content || r.excerpt, fetched: true };
                        }
                    } catch {}
                    return { ...r, fetched: true };
                })).then(enriched => setSemanticResults(enriched));
            }
        } catch {
            setSemanticResults([]);
        } finally {
            setSemanticLoading(false);
        }
    };

    const previewW = isHoverExpanded ? PREVIEW_W_EXPANDED : PREVIEW_W_NORMAL;
    const previewLeft = hover
        ? Math.max(8, Math.min(window.innerWidth - previewW - 8, hover.left - (isHoverExpanded ? (PREVIEW_W_EXPANDED - PREVIEW_W_NORMAL) / 2 : 0)))
        : 0;

    return (
        <>
            {/* Bottom strip */}
            <div className="fixed bottom-0 left-0 right-0 z-[200] bg-white dark:bg-slate-900 border-t-2 border-indigo-100 dark:border-indigo-900/50 shadow-[0_-8px_30px_rgba(0,0,0,0.12)] dark:shadow-[0_-8px_30px_rgba(0,0,0,0.4)] animate-in slide-in-from-bottom-3 duration-200">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 dark:border-slate-800">
                    <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300 text-xs font-bold rounded-full border border-indigo-200 dark:border-indigo-700/50">
                            <Hash size={11} />
                            {displayTag.replace(/^#/, '')}
                        </span>
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                            {notes.length} {isVi ? 'ghi chú liên quan' : 'related notes'}
                        </span>
                        <button
                            onClick={handleSemanticSearch}
                            disabled={semanticLoading || semanticSearched}
                            className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors border ${
                                semanticSearched
                                    ? 'bg-violet-50 dark:bg-violet-900/20 text-violet-400 dark:text-violet-500 border-violet-200 dark:border-violet-800 cursor-default'
                                    : 'bg-white dark:bg-slate-800 text-violet-600 dark:text-violet-400 border-violet-200 dark:border-violet-700 hover:bg-violet-50 dark:hover:bg-violet-900/20'
                            } disabled:opacity-60`}
                        >
                            {semanticLoading ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />}
                            {isVi ? 'Tìm thêm bằng AI' : 'Find more with AI'}
                        </button>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                        <X size={16} />
                    </button>
                </div>

                {/* Exact-match cards */}
                {notes.length === 0 ? (
                    <div className="px-4 py-4 text-xs text-gray-400 dark:text-gray-500">
                        {isVi ? 'Không tìm thấy ghi chú nào.' : 'No notes found.'}
                    </div>
                ) : (
                    <div className="flex gap-3 overflow-x-auto px-4 py-3" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                        {notes.map(note => {
                            const isLinked = linkedNodeIds?.has(note.id) ?? false;
                            return (
                                <div key={note.id} className="relative flex-shrink-0 w-36 group"
                                    onMouseEnter={(e) => showHover(note, e.currentTarget as HTMLElement)}
                                    onMouseLeave={hideHover}
                                >
                                    <button
                                        onClick={() => navigateToNote(note.id)}
                                        className="w-full text-left rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 hover:border-indigo-400 dark:hover:border-indigo-500 hover:shadow-md transition-all overflow-hidden"
                                    >
                                        {getFirstImage(note) ? (
                                            <div className="w-full h-20 overflow-hidden bg-white dark:bg-slate-900">
                                                <img src={getFirstImage(note)!} alt="" className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-200" draggable={false} />
                                            </div>
                                        ) : (
                                            <div className="w-full h-20 bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-slate-700 dark:to-slate-600 flex items-center justify-center">
                                                <Hash size={22} className="text-indigo-200 dark:text-indigo-500" />
                                            </div>
                                        )}
                                        <div className="px-2.5 py-2">
                                            <p className="text-[11px] font-semibold text-gray-800 dark:text-gray-100 line-clamp-2 leading-tight">
                                                {note.title || (isVi ? 'Không có tiêu đề' : 'Untitled')}
                                            </p>
                                            {note.content && (
                                                <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 line-clamp-2 leading-relaxed">
                                                    {note.content.replace(/[#*_`\[\]]/g, '').slice(0, 80)}
                                                </p>
                                            )}
                                        </div>
                                    </button>
                                    {onCreateLink && (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); onCreateLink(note.id); }}
                                            className={`absolute top-1.5 right-1.5 z-10 p-1 rounded-md backdrop-blur-sm shadow-sm border transition-all opacity-0 group-hover:opacity-100 ${isLinked ? 'bg-indigo-500 border-indigo-400 text-white' : 'bg-white/90 dark:bg-slate-700/90 border-gray-200 dark:border-slate-600 text-gray-400 hover:bg-indigo-50 hover:text-indigo-600 dark:hover:bg-indigo-900/40 dark:hover:text-indigo-400'}`}
                                            title={isLinked ? (isVi ? 'Đã liên kết' : 'Already linked') : (isVi ? 'Tạo cross-link' : 'Cross-link')}
                                        >
                                            <Link2 size={11} />
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* AI Semantic results */}
                {semanticSearched && (
                    <div className="border-t border-violet-100 dark:border-violet-900/30">
                        <div className="flex items-center gap-2 px-4 py-2">
                            <Sparkles size={10} className="text-violet-400 shrink-0" />
                            <span className="text-[11px] text-violet-500 dark:text-violet-400 font-semibold">
                                {isVi ? 'Gợi ý AI — có thể liên quan về ý nghĩa (không có hashtag này)' : 'AI suggestions — possibly related in meaning (no exact tag)'}
                            </span>
                            {semanticLoading && <Loader2 size={10} className="animate-spin text-violet-400 shrink-0" />}
                        </div>
                        {!semanticLoading && semanticResults.length === 0 && (
                            <p className="px-4 pb-3 text-xs text-gray-400 dark:text-gray-500">
                                {isVi ? 'Không tìm thấy gợi ý nào.' : 'No suggestions found.'}
                            </p>
                        )}
                        {semanticResults.length > 0 && (
                            <div className="flex gap-3 overflow-x-auto px-4 pb-3" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                                {semanticResults.map(r => (
                                    <div key={r.nodeId} className="relative flex-shrink-0 w-36 group"
                                        onMouseEnter={(e) => showSemanticHover(r, e.currentTarget as HTMLElement)}
                                        onMouseLeave={hideHover}
                                    >
                                        <button
                                            onClick={() => {
                                                if (onNavigateCrossNotebook) onNavigateCrossNotebook(r.notebookId, r.nodeId);
                                                else onNavigate(r.nodeId);
                                                onClose();
                                            }}
                                            className="w-full text-left rounded-xl border border-dashed border-violet-300 dark:border-violet-700 bg-violet-50/30 dark:bg-violet-900/10 hover:border-violet-500 dark:hover:border-violet-500 hover:shadow-md transition-all overflow-hidden"
                                        >
                                            {/* Image or placeholder */}
                                            {r.imageBase64 ? (
                                                <div className="w-full h-20 overflow-hidden bg-white dark:bg-slate-900">
                                                    <img src={r.imageBase64} alt="" className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-200" draggable={false} />
                                                </div>
                                            ) : (
                                                <div className="w-full h-20 bg-gradient-to-br from-violet-50 to-indigo-50 dark:from-violet-900/20 dark:to-indigo-900/20 flex flex-col items-center justify-center gap-1">
                                                    {!r.fetched
                                                        ? <Loader2 size={16} className="text-violet-300 animate-spin" />
                                                        : <Sparkles size={16} className="text-violet-300 dark:text-violet-600" />
                                                    }
                                                    <span className="text-[10px] font-bold text-violet-400 dark:text-violet-500 tabular-nums">
                                                        {Math.round(r.similarity * 100)}%
                                                    </span>
                                                </div>
                                            )}
                                            <div className="px-2.5 py-2">
                                                <p className="text-[9px] text-violet-400 dark:text-violet-500 truncate mb-0.5">{r.notebookTitle}</p>
                                                <p className="text-[11px] font-semibold text-gray-800 dark:text-gray-100 line-clamp-2 leading-tight">
                                                    {r.nodeTitle || (isVi ? 'Không có tiêu đề' : 'Untitled')}
                                                </p>
                                                {(r.fullContent || r.excerpt) && (
                                                    <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 line-clamp-2 leading-relaxed">
                                                        {(r.fullContent || r.excerpt || '').replace(/[#*_`\[\]]/g, '').slice(0, 80)}
                                                    </p>
                                                )}
                                                {r.imageBase64 && (
                                                    <span className="inline-block mt-1 text-[9px] font-bold text-violet-400 tabular-nums">{Math.round(r.similarity * 100)}% phù hợp</span>
                                                )}
                                            </div>
                                        </button>
                                        {onCreateLink && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); onCreateLink(r.nodeId); }}
                                                className="absolute top-1.5 right-1.5 z-10 p-1 rounded-md backdrop-blur-sm shadow-sm border transition-all opacity-0 group-hover:opacity-100 bg-white/90 dark:bg-slate-700/90 border-violet-200 dark:border-violet-700 text-violet-400 hover:bg-violet-50 hover:text-violet-600 dark:hover:bg-violet-900/40"
                                                title={isVi ? 'Tạo cross-link' : 'Cross-link'}
                                            >
                                                <Link2 size={11} />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Floating hover preview */}
            {hover && (
                <div
                    style={{ position: 'fixed', top: hover.top, left: previewLeft, width: previewW, zIndex: 9999, transition: 'width 0.2s ease, left 0.2s ease' }}
                    className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-700 overflow-hidden"
                    onMouseEnter={clearTimer}
                    onMouseLeave={hideHover}
                >
                    <div className="absolute top-2 right-2 z-10 flex items-center gap-1">
                        <button
                            onMouseDown={(e) => {
                                e.preventDefault();
                                clearTimer();
                                const alreadyPinned = pinnedPreviews.some(p => p.node.id === hover.node.id);
                                if (!alreadyPinned) {
                                    setPinnedPreviews(prev => [...prev, { id: `pin-${Date.now()}`, node: hover.node, x: previewLeft, y: hover.top, expanded: isHoverExpanded }]);
                                }
                                setHover(null);
                                setIsHoverExpanded(false);
                            }}
                            className="p-1.5 rounded-lg bg-white/90 dark:bg-slate-700/90 backdrop-blur-sm hover:bg-indigo-50 dark:hover:bg-indigo-900/40 text-gray-500 hover:text-indigo-600 dark:text-gray-300 dark:hover:text-indigo-400 shadow-sm border border-gray-200 dark:border-slate-600 transition-all"
                            title={isVi ? 'Ghim cửa sổ này' : 'Pin this window'}
                        ><Pin size={13} /></button>
                        <button
                            onMouseDown={(e) => { e.preventDefault(); setIsHoverExpanded(v => !v); }}
                            className="p-1.5 rounded-lg bg-white/90 dark:bg-slate-700/90 backdrop-blur-sm hover:bg-white dark:hover:bg-slate-600 text-gray-500 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white shadow-sm border border-gray-200 dark:border-slate-600 transition-all"
                            title={isHoverExpanded ? (isVi ? 'Thu nhỏ' : 'Collapse') : (isVi ? 'Mở rộng' : 'Expand')}
                        >{isHoverExpanded ? <Minimize2 size={13} /> : <Maximize2 size={13} />}</button>
                        {onCreateLink && (() => {
                            const isLinked = linkedNodeIds?.has(hover.node.id) ?? false;
                            return (
                                <button
                                    onMouseDown={(e) => { e.preventDefault(); onCreateLink(hover.node.id); }}
                                    className={`p-1.5 rounded-lg backdrop-blur-sm shadow-sm border transition-all ${isLinked ? 'bg-indigo-500 border-indigo-400 text-white' : 'bg-white/90 dark:bg-slate-700/90 hover:bg-indigo-50 dark:hover:bg-indigo-900/40 text-gray-500 hover:text-indigo-600 dark:text-gray-300 dark:hover:text-indigo-400 border-gray-200 dark:border-slate-600'}`}
                                    title={isLinked ? (isVi ? 'Đã liên kết' : 'Already linked') : (isVi ? 'Tạo cross-link' : 'Cross-link')}
                                ><Link2 size={13} /></button>
                            );
                        })()}
                        <button
                            onMouseDown={(e) => { e.preventDefault(); navigateToNote(hover.node.id); }}
                            className="p-1.5 rounded-lg bg-white/90 dark:bg-slate-700/90 backdrop-blur-sm hover:bg-indigo-50 dark:hover:bg-indigo-900/40 text-gray-500 hover:text-indigo-600 dark:text-gray-300 dark:hover:text-indigo-400 shadow-sm border border-gray-200 dark:border-slate-600 transition-all"
                            title={isVi ? 'Mở ghi chú' : 'Open note'}
                        ><ExternalLink size={13} /></button>
                    </div>

                    {getFirstImage(hover.node) && (
                        <img src={getFirstImage(hover.node)!} alt="" className="w-full object-contain border-b border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-900" style={{ maxHeight: isHoverExpanded ? 280 : 176 }} />
                    )}
                    <div className="p-3.5 overflow-y-auto" style={{ maxHeight: isHoverExpanded ? 360 : 256 }} onWheel={(e) => e.stopPropagation()}>
                        {hover.node.title && (
                            <div className="font-semibold text-sm text-gray-900 dark:text-white mb-2 pr-16">{hover.node.title}</div>
                        )}
                        {hover.node.content ? (
                            <div className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed [&>p]:my-1 [&>ul]:my-1 [&>ol]:my-1 [&>ul]:pl-3 [&>ol]:pl-3 [&>li]:my-0.5 [&>h1]:font-semibold [&>h2]:font-semibold [&>h3]:font-semibold" dangerouslySetInnerHTML={{ __html: parseMarkdown(hover.node.content) }} />
                        ) : (
                            <p className="text-xs text-gray-300 dark:text-gray-600 italic">{isVi ? 'Không có nội dung' : 'No content'}</p>
                        )}
                    </div>
                </div>
            )}

            {/* Pinned preview windows */}
            {pinnedPreviews.map(pinned => {
                const cardW = pinned.expanded ? 500 : 300;
                const cardH = pinned.expanded ? 640 : 420;
                return (
                    <div key={pinned.id} style={{ position: 'fixed', top: pinned.y, left: pinned.x, width: cardW, maxHeight: cardH, zIndex: 9998, transition: 'width 0.2s ease, max-height 0.2s ease' }} className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl border-2 border-indigo-200 dark:border-indigo-800 overflow-hidden flex flex-col">
                        <div className="h-7 bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-slate-700 dark:to-slate-700 rounded-t-xl cursor-grab active:cursor-grabbing flex items-center px-2.5 gap-1.5 border-b border-indigo-100 dark:border-slate-600 shrink-0 select-none"
                            onMouseDown={(e) => { draggingPinRef.current = { id: pinned.id, offsetX: e.clientX - pinned.x, offsetY: e.clientY - pinned.y }; e.preventDefault(); }}>
                            <Pin size={10} className="text-indigo-400 shrink-0" />
                            <span className="text-[11px] text-indigo-500 dark:text-indigo-400 font-medium truncate flex-1">{pinned.node.title || 'Untitled'}</span>
                        </div>
                        <button onClick={() => setPinnedPreviews(prev => prev.map(p => p.id === pinned.id ? { ...p, expanded: !p.expanded } : p))} className="absolute top-0.5 right-8 z-10 p-1 rounded-md hover:bg-indigo-100 dark:hover:bg-slate-600 text-indigo-400 transition-colors" title={pinned.expanded ? (isVi ? 'Thu nhỏ' : 'Collapse') : (isVi ? 'Mở rộng' : 'Expand')}>
                            {pinned.expanded ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
                        </button>
                        <button onClick={() => setPinnedPreviews(prev => prev.filter(p => p.id !== pinned.id))} className="absolute top-0.5 right-1 z-10 p-1 rounded-md hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-500 transition-colors" title={isVi ? 'Đóng' : 'Close'}>
                            <X size={12} />
                        </button>
                        {getFirstImage(pinned.node) && <img src={getFirstImage(pinned.node)!} alt="" className="w-full object-contain border-b border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-900 shrink-0" style={{ maxHeight: pinned.expanded ? 280 : 176 }} />}
                        <div className="p-3.5 overflow-y-auto flex-1" onWheel={(e) => e.stopPropagation()}>
                            {pinned.node.title && <div className="font-semibold text-sm text-gray-900 dark:text-white mb-2">{pinned.node.title}</div>}
                            {pinned.node.content
                                ? <div className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed [&>p]:my-1 [&>ul]:my-1 [&>ol]:my-1 [&>ul]:pl-3 [&>ol]:pl-3 [&>h1]:font-semibold [&>h2]:font-semibold [&>h3]:font-semibold" dangerouslySetInnerHTML={{ __html: parseMarkdown(pinned.node.content) }} />
                                : <p className="text-xs text-gray-300 dark:text-gray-600 italic">{isVi ? 'Không có nội dung' : 'No content'}</p>}
                        </div>
                    </div>
                );
            })}
        </>
    );
};
